import { BasicColumnTypes } from '@opengov/ogql-types';
import {
  AggregationType,
  BooleanOperators,
  COLUMN_GROUP_ORGANIZE_BY_INDEX,
  getOrganizeByProjections,
  EAllLedgerType,
  EExpressionDisplayType,
  EGroupType,
  ELedgerType,
  EOgqlAggregateColumnOperators,
  EReportChartType,
  EReportColumnTypes,
  FilterOperators,
  IAccountSummary,
  IAccountSummaryChart,
  IColumnAggregation,
  IColumnDataSource,
  IColumnFilter,
  IColumnFilterBooleanOperator,
  IColumnGroupDateFilter,
  IColumnSource,
  IControlBlockAlias,
  IDimensionColumn,
  IGroupExpression,
  ILensColumnReference,
  IPercentOfTotalReportColumn,
  IProjection,
  IRelation,
  IReportConfiguration,
  IRollingNumRangeOption,
  IRowGroup,
  InSetFilterOperator,
  MultiHierarchyAccountSummary,
  MultiHierarchyCalculatedColumnGroup,
  MultiHierarchyCalculatedRowGroup,
  MultiHierarchyCoaFilter,
  MultiHierarchyColumnDataSource,
  MultiHierarchyCompositeFilter,
  MultiHierarchyGroupingLevel,
  MultiHierarchyReportFilter,
  MultiHierarchyRowGroup,
  MultiHierarchyRowOverride,
  PublicMhDrillCapability,
  RelativeFilter,
  SortDirection,
  TOTALS_COLUMN_VALUE_KEY,
  UnifiedLedgerType,
  addPublicMhDrillCapability,
  createColumnGroups,
  createControlBlockIndex,
  getColumnValueMappingKey,
  getLastRowGroupAsCoA,
  getLastRowGroupAsOverride,
  getMultiHierarchyFirstRootRowId,
  getYAxisSource,
  isCalculationExpression,
  isColumnDataSource,
  isMultiHierarchyAccountSummary,
  isMultiHierarchyCalculatedColumnGroup,
  isMultiHierarchyColumnDataSource,
  isMultiHierarchyRowGroup,
  isMultiHierarchyRowOverride,
  isRowOverride,
  removePublicMhDrillCapabilityAndDescendants
} from '@opengov/reporting-types';
import {
  IReportColumn,
  createAggregateReportColumn,
  createPercentOfTotalReportColumn
} from '@opengov/reporting-types/src/reportColumns-immer';
import { GridApi, IRowNode, RowNode } from 'ag-grid-community';
import { Draft, castDraft } from 'immer';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { IRollingRangeOption } from '../../../src/components/editor/filterForm/hooks/useDateRangeOptions';
import {
  GroupByConfig,
  isMultiHierarchyGroupByConfig
} from '../../../src/components/editor/sidepanel/columnEditor/ColumnEditorTypes';
import { GROUPING_LIMIT } from '../../../src/components/editor/sidepanel/rowEditor/useSetupForm';
import { AG_GRID_GROUP_COLUMN_ID } from '../../../src/components/table/agGridConsts';
import { derivePublicMhDrillCapability } from '../../../src/components/table/derivePublicMhDrillCapability';
import {
  addExpandedState,
  createNodePath,
  getDeepestExpandedLevel,
  removeExpandedState,
  removeExpandedStateAtLevel
} from '../../../src/components/table/gridUtils';
import { collectNodesToExpandOneLevel, isEligibleNode } from '../../../src/components/table/expandOneLevel';
import { isInjectedRootBreakdownRow } from '../../../src/components/table/multiHierarchyRootBreakdown';
import { createChartConfigForChartType, switchChartType } from '../../../src/components/viz/utils';
import { IGroupBy, IRowEditorFormValues } from '../../../src/types/MultiDatasetTypes';
import { createDuplicateName } from '../helpers/duplicateHelpers';
import { buildGroupingProjection } from './buildGroupingProjection';
import {
  getAllColumnAlias,
  getMultiHierarchyRootRowId,
  isSameColumnFilter,
  mergeColumnValueMapping,
  removeAndUpdateBlocks,
  updateCalcColumnGroupDependencies
} from './utils';
import { pruneSeriesColorsForDeletedGroups } from './seriesColors';
import { setVisualizationQueryCategorizedByColumn } from './visualizationQueryActions';

// A chart series holds the id of the column group it was built from. Left behind after the group
// is deleted, that dangling id has no label and no data, so drop the affected series.
function removeColumnGroupFromCharts(charts: Draft<IAccountSummaryChart>[], columnGroupId: string): void {
  charts.forEach((chart) => {
    chart.yAxis.series = chart.yAxis.series.filter(([seriesGroupId]) => seriesGroupId !== columnGroupId);

    // The secondary axis references row groups instead when the chart is organized by columns.
    const secondaryAxis = chart.yAxis.multipleAxis;
    if (!secondaryAxis?.series || getYAxisSource(chart) !== 'cols') return;
    secondaryAxis.series = secondaryAxis.series.filter(([seriesGroupId]) => seriesGroupId !== columnGroupId);
  });
}

// column group
export function removeColumnGroupByIndex(draft: Draft<IReportConfiguration>, index: number): void {
  if (index === null || draft.accountSummary.columnGroups.length <= 1) return;
  const [deletedColumnGroup] = draft.accountSummary.columnGroups.splice(index, 1);
  if (!deletedColumnGroup.id) return;
  // Both flavours of override row key their per-column overrides by parent column group id, and
  // `isRowOverride` only matches the non-ERP discriminant — so the ERP type is checked separately.
  // Without this, ERP override rows keep dangling references to deleted parent column groups.
  draft.accountSummary.rowGroups.forEach((rowGroup) => {
    if (!isRowOverride(rowGroup) && !isMultiHierarchyRowOverride(rowGroup)) return;
    const { [deletedColumnGroup.id]: _, ...rest } = rowGroup.columnGroups;
    rowGroup.columnGroups = castDraft(rest);
  });
  removeColumnGroupFromCharts(draft.accountSummary.charts ?? [], deletedColumnGroup.id);
  pruneSeriesColorsForDeletedGroups(draft.accountSummary);
}

export type IEditColumnGroupByIndexPayload = { index: number | null; columnGroup: IColumnSource };

export function editColumnOrder(
  draft: Draft<IReportConfiguration>,
  payload: { columnState: { colId: string }[] }
): void {
  //if the locked group column was moved to the right, put it back
  const lockedIndex = payload.columnState.findIndex((column) => column.colId === AG_GRID_GROUP_COLUMN_ID);
  const columnState = payload.columnState.slice();
  if (lockedIndex > 0) {
    const [column] = columnState.splice(lockedIndex, 1);
    columnState.unshift(column);
  }
  draft.accountSummary.columnOrder = Object.fromEntries(columnState.map((column, index) => [column.colId, index]));
}

export function onColumnOrderChange(draft: Draft<IReportConfiguration>, columnOrder: Record<string, number>) {
  draft.accountSummary.columnOrder = columnOrder;
}

export function onColumnVisibilityChange(draft: Draft<IReportConfiguration>, hiddenColumns: Record<string, boolean>) {
  draft.accountSummary.hiddenColumns = hiddenColumns;
}

// Used for editing calculated column groups
export function editColumnGroupByIndex(
  draft: Draft<IReportConfiguration>,
  payload: IEditColumnGroupByIndexPayload
): void {
  const columnGroups = draft.accountSummary.columnGroups;
  const index = payload.index ?? columnGroups.length;
  const group = cloneDeep(payload.columnGroup);
  if (!payload.columnGroup.id) {
    group.id = uuidv4();
  }
  (group as IGroupExpression).alias = (group as IGroupExpression).alias?.trim();

  // Get all column names/alias before and after updated column groups
  const previousColumnGroupNames = getAllColumnAlias(draft);
  draft.accountSummary.columnGroups[index] = castDraft(group);
  const currentColumnGroupNames = getAllColumnAlias(draft);

  // Find out what column alias changed and its original value
  const originalColumnGroupName = previousColumnGroupNames.filter((name) => !currentColumnGroupNames.includes(name))[0];
  const updatedColumnGroupName = currentColumnGroupNames.filter((name) => !previousColumnGroupNames.includes(name))[0];

  if (originalColumnGroupName && updatedColumnGroupName && originalColumnGroupName !== updatedColumnGroupName) {
    const updatedAccountSummaryGroups = updateCalcColumnGroupDependencies(
      draft.accountSummary.columnGroups,
      originalColumnGroupName,
      updatedColumnGroupName
    );
    draft.accountSummary.columnGroups = updatedAccountSummaryGroups;
  }
}

/**
 * Replaces every organize-by level, keeping the header-label constant at index 0.
 *
 * Splices rather than assigning by index, so removing a level TRUNCATES the list. Assignment alone
 * would overwrite the levels the user kept and silently leave a deeper one behind, still driving a
 * column breakdown that is no longer in the editor.
 */
function setOrganizeByProjections(columnGroup: { grouping: IProjection[] }, projections: IProjection[]): void {
  columnGroup.grouping.splice(COLUMN_GROUP_ORGANIZE_BY_INDEX, columnGroup.grouping.length, ...projections);
}

/** The organize-by levels a payload is asking for, as projections. */
function incomingOrganizeByProjections(payload: {
  groupByConfig: GroupByConfig;
  groupByConfigs?: GroupByConfig[];
}): IProjection[] {
  return (payload.groupByConfigs ?? [payload.groupByConfig]).map(buildGroupingProjection);
}

/**
 * Writes the organize-by from a payload.
 *
 * Only a payload that carries the whole level list may TRUNCATE — that is the caller which knows how
 * many levels the user chose. A payload carrying just the singular `groupByConfig` (the column-value
 * rename path, and the non-MH editor) assigns level 1 in place: it has no opinion about the deeper
 * levels, and splicing on its behalf silently deleted them.
 */
function applyIncomingOrganizeBy(
  columnGroup: IColumnDataSource,
  payload: { groupByConfig: GroupByConfig; groupByConfigs?: GroupByConfig[] }
): void {
  const projections = incomingOrganizeByProjections(payload);
  if (payload.groupByConfigs) {
    setOrganizeByProjections(columnGroup, projections);
    return;
  }
  columnGroup.grouping[COLUMN_GROUP_ORGANIZE_BY_INDEX] = projections[0];
}

function updateLensColumnGroup(columnGroup: IColumnSource, payload: ICreateAccountSummaryColumnGroupPayload) {
  const { datasourceId, name, amount, aggregation, groupByConfig } = payload;

  (columnGroup as IColumnDataSource).lens.id = datasourceId;
  (columnGroup as IColumnDataSource).grouping[0] = { constant: name };
  (columnGroup as IColumnDataSource).measureColumn = { columnName: amount };
  // Preserve MultiHierarchy type — a multi-hierarchy column group stays MultiHierarchy
  // regardless of whether it organizes by dimension or by segment hierarchy.
  if (!isMultiHierarchyColumnDataSource(columnGroup)) {
    (columnGroup as IColumnDataSource).type = isMultiHierarchyGroupByConfig(groupByConfig)
      ? EGroupType.MultiHierarchy
      : EGroupType.Lens;
  }
  if (aggregation) {
    (columnGroup as IColumnAggregation).aggregation = aggregation;
  }

  if (
    !isCalculationExpression(columnGroup) &&
    !isMultiHierarchyCalculatedColumnGroup(columnGroup) &&
    !payload.columnValueMapping
  ) {
    columnGroup.filters = payload.filters;
    columnGroup.dateFilter = payload.dateFilter;
    columnGroup.relativeFilter = payload.relativeFilter;
    columnGroup.rollingNumRangeOption = payload.rollingNumRangeOption;
    (columnGroup as { relativeRolling?: string }).relativeRolling = payload.relativeRolling;
  }

  applyIncomingOrganizeBy(columnGroup as IColumnDataSource, payload);

  if (payload.columnValueMapping) {
    (columnGroup as IColumnDataSource).columnValueMapping = mergeColumnValueMapping(
      (columnGroup as IColumnDataSource).columnValueMapping,
      payload.columnValueMapping
    );
  }

  return columnGroup;
}

// For dataset-sourced
export function updateAccountSummaryColumnGroup(
  draft: Draft<IReportConfiguration>,
  payload: IUpdateAccountSummaryColumnGroupPayload
): void {
  const { columnGroupIndex } = payload;
  const columnGroups = draft.accountSummary.columnGroups;

  const previousColumnGroupNames = getAllColumnAlias(draft);

  columnGroups[columnGroupIndex] = castDraft(updateLensColumnGroup(columnGroups[columnGroupIndex], payload));

  const currentColumnGroupNames = getAllColumnAlias(draft);
  // Find out what column alias changed and its original value
  const originalColumnGroupName = previousColumnGroupNames.find((name) => !currentColumnGroupNames.includes(name));
  const updatedColumnGroupName = currentColumnGroupNames.find((name) => !previousColumnGroupNames.includes(name));

  if (originalColumnGroupName && updatedColumnGroupName && originalColumnGroupName !== updatedColumnGroupName) {
    const updatedAccountSummaryGroups = updateCalcColumnGroupDependencies(
      draft.accountSummary.columnGroups,
      originalColumnGroupName,
      updatedColumnGroupName
    );
    draft.accountSummary.columnGroups = updatedAccountSummaryGroups;
  }
}

export function setColumnGroupFilters(
  draft: Draft<IReportConfiguration>,
  payload: IUpdateAccountSummaryColumnGroupPayload
): void {
  const currentColumnGroup = draft.accountSummary.columnGroups[payload.columnGroupIndex];
  if (isColumnDataSource(currentColumnGroup)) {
    currentColumnGroup.filters = payload.filters;
    currentColumnGroup.dateFilter = payload.dateFilter;
    currentColumnGroup.relativeFilter = payload.relativeFilter;
    currentColumnGroup.rollingNumRangeOption = payload.rollingNumRangeOption;
    if (isMultiHierarchyColumnDataSource(currentColumnGroup)) {
      currentColumnGroup.relativeRolling = payload.relativeRolling;
    }
  }
}

export function setViewFilters(draft: Draft<IReportConfiguration>, payload): void {
  draft.accountSummary.filters = payload.filters;
}

type SetMultiHierarchyViewFiltersPayload = {
  filters: MultiHierarchyCoaFilter[];
};

export function setMultiHierarchyViewFilters(
  draft: Draft<IReportConfiguration>,
  payload: SetMultiHierarchyViewFiltersPayload
): void {
  (draft.accountSummary as Draft<MultiHierarchyAccountSummary>).filters =
    payload.filters as Draft<MultiHierarchyAccountSummary>['filters'];
}

export interface ICreateMultiHierarchyColumnGroupPayload {
  datasourceId: string;
  amount: string;
  name: string;
  groupByConfig: GroupByConfig;
  /** Every organize-by level, outermost first. When absent, `groupByConfig` is used as a single level. */
  groupByConfigs?: GroupByConfig[];
  /** CoA-segment and/or non-CoA lens-column filters; the query pipelines partition them by shape. */
  filters?: MultiHierarchyReportFilter[];
  dateFilter?: IColumnGroupDateFilter;
  relativeFilter?: RelativeFilter;
  rollingNumRangeOption?: IRollingRangeOption;
  relativeRolling?: string;
  showTotalsColumn?: boolean;
}

export function createMultiHierarchyColumnGroup(
  draft: Draft<IReportConfiguration>,
  payload: ICreateMultiHierarchyColumnGroupPayload
): void {
  if (!draft.accountSummary || !isMultiHierarchyAccountSummary(draft.accountSummary)) return;
  const projections = (payload.groupByConfigs ?? [payload.groupByConfig]).map(buildGroupingProjection);
  const columnGroup: MultiHierarchyColumnDataSource = {
    id: uuidv4(),
    type: EGroupType.MultiHierarchy,
    lens: { id: payload.datasourceId },
    measureColumn: { columnName: payload.amount },
    grouping: [{ constant: payload.name }, ...projections],
    filters: payload.filters,
    dateFilter: payload.dateFilter,
    relativeFilter: payload.relativeFilter,
    rollingNumRangeOption: payload.rollingNumRangeOption,
    relativeRolling: payload.relativeRolling,
    showTotalsColumn: payload.showTotalsColumn
  };
  draft.accountSummary.columnGroups.push(castDraft(columnGroup));
}

export interface ICreateAccountSummaryColumnGroupPayload {
  datasourceId: string;
  amount: string;
  name: string;
  groupByConfig: GroupByConfig;
  /**
   * Every organize-by level, outermost first. Set only by the multi-hierarchy column editor; the
   * create path and the non-MH editor keep sending the singular `groupByConfig`, which is read as a
   * one-level list.
   */
  groupByConfigs?: GroupByConfig[];
  filters: any;
  dateFilter: any;
  relativeFilter: RelativeFilter;
  aggregation: AggregationType;
  columnValueMapping?: Record<string, Record<string, string>>;
  rollingNumRangeOption: IRollingRangeOption;
  relativeRolling?: string;
}

export interface IUpdateAccountSummaryColumnGroupPayload extends ICreateAccountSummaryColumnGroupPayload {
  columnGroupIndex: number;
}

// TODO: add tests
export function createAccountSummaryColumnGroup(
  draft: Draft<IReportConfiguration>,
  payload: ICreateAccountSummaryColumnGroupPayload
): void {
  const { name, datasourceId, amount, aggregation, groupByConfig } = payload;
  const columnGroup = {
    id: uuidv4(),
    lens: { id: datasourceId },
    measureColumn: { columnName: amount },
    grouping: [{ constant: name }],
    type: isMultiHierarchyGroupByConfig(groupByConfig) ? EGroupType.MultiHierarchy : EGroupType.Lens
  } as IColumnDataSource;

  if (aggregation) {
    (columnGroup as IColumnAggregation).aggregation = aggregation;
  }

  columnGroup.grouping.push(buildGroupingProjection(groupByConfig));

  // We the type as Lens above, so we're creating a datasource column group.
  // We set properties here based on that.
  columnGroup.filters = payload.filters;
  columnGroup.dateFilter = payload.dateFilter;
  columnGroup.relativeFilter = payload.relativeFilter;
  columnGroup.rollingNumRangeOption = payload.rollingNumRangeOption;
  (columnGroup as { relativeRolling?: string }).relativeRolling = payload.relativeRolling;

  if (!draft.accountSummary || isMultiHierarchyAccountSummary(draft.accountSummary)) {
    return;
  }
  const accountSummary = draft.accountSummary;
  accountSummary.columnGroups.push(castDraft(columnGroup));
}

// TODO: add tests
export function setDimensionalSortColumn(
  draft: Draft<IReportConfiguration>,
  payload: { columnName: string; direction: SortDirection }
): void {
  draft.dimensionalSummaryQueries[0].sortColumnName = payload.columnName;
  draft.dimensionalSummaryQueries[0].sortDirection = payload.direction;
}

export function addAccountSummaryRowGroup(state: Draft<IReportConfiguration>, payload: IRowGroup): void {
  if (!state.accountSummary || isMultiHierarchyAccountSummary(state.accountSummary)) {
    return;
  }
  const accountSummary = state.accountSummary;
  accountSummary.rowGroups.push(payload);
}

function groupingFromGroupColumn(
  groupColumn: IGroupBy,
  coaDimension: IRelation
): ILensColumnReference | IDimensionColumn {
  const splitGroupByColumn = groupColumn.column.split(':');

  const groupByDimension = groupColumn.dimensionId ? { id: groupColumn.dimensionId } : undefined;

  if (splitGroupByColumn.length === 2) {
    // When groupBy.column is 'joinColumn:dimensionColumn' but groupBy.dimensionId is undefined,
    // preserve behavior of assuming the column grouping is the CoA dimension
    return { columnName: splitGroupByColumn[1], dimension: groupByDimension ?? coaDimension };
  } else if (splitGroupByColumn.length === 1) {
    if (groupByDimension) {
      return { columnName: splitGroupByColumn[0], dimension: groupByDimension };
    } else {
      return { columnName: splitGroupByColumn[0] };
    }
  } else {
    throw new Error(`Invalid column name: ${groupColumn.column}`);
  }
}

export function addNewRowGroup(draft: Draft<IReportConfiguration>, payload: IRowEditorFormValues): void {
  if (!draft.accountSummary || isMultiHierarchyAccountSummary(draft.accountSummary)) {
    return;
  }
  const accountSummary = draft.accountSummary;

  const newRowGroup = JSON.parse(JSON.stringify(getLastRowGroupAsCoA(accountSummary.rowGroups, null))) as IRowGroup<
    UnifiedLedgerType | ELedgerType[]
  >;

  newRowGroup.id = uuidv4();
  newRowGroup.showOnly.value = payload.show;
  newRowGroup.groupName = payload.groupName;
  newRowGroup.filters = payload.filters;
  newRowGroup.showOnly.operator = FilterOperators.Equal;

  newRowGroup.grouping = payload.groupBy.map((groupColumn) =>
    groupingFromGroupColumn(groupColumn, newRowGroup.showOnly.dimension)
  );
  if (payload.show === EAllLedgerType.al) {
    newRowGroup.showOnly.value = Object.values(ELedgerType);
    newRowGroup.showOnly.operator = InSetFilterOperator.InSet;
  }
  accountSummary.rowGroups.push(newRowGroup as IRowGroup);
}

export function editCalcRowGroupByIndex(
  draft: Draft<IReportConfiguration>,
  payload: { rowGroup: IGroupExpression; index: number }
): void {
  const rowGroups = draft.accountSummary.rowGroups as Draft<IGroupExpression>[];
  const { index } = payload;
  if (index === rowGroups.length) {
    rowGroups.push(castDraft({ ...payload.rowGroup, id: uuidv4() }));
  } else {
    rowGroups[index] = castDraft(payload.rowGroup);
  }
}

export function setRowGroupFilters(draft: Draft<IReportConfiguration>, payload): void {
  (draft.accountSummary.rowGroups[payload.columnRowIndex] as IRowGroup).filters = payload.filters;
}

export function removeRowGroup(draft: Draft<IReportConfiguration>, payload: number): void {
  draft.accountSummary.rowGroups.splice(payload, 1);
  pruneSeriesColorsForDeletedGroups(draft.accountSummary);
}

export function duplicateCalcRowGroup(draft: Draft<IReportConfiguration>, payload: number): void {
  const rowGroups = draft.accountSummary.rowGroups as Draft<IGroupExpression>[];
  const currentRowGroup = rowGroups[payload];
  const alias = createDuplicateName(currentRowGroup.alias);
  rowGroups.push({ ...currentRowGroup, alias, id: uuidv4() });
}

type IUpdateRowGroupPayload = { index: number; values: IRowEditorFormValues };

export function updateRowGroup(draft: Draft<IReportConfiguration>, payload: IUpdateRowGroupPayload): void {
  const { values, index } = payload;
  const existingRowGroup = draft.accountSummary.rowGroups[index] as IRowGroup<UnifiedLedgerType | ELedgerType[]>;
  const groupColumns = payload.values.groupBy.map((groupColumn) =>
    groupingFromGroupColumn(groupColumn, existingRowGroup.showOnly.dimension)
  );
  existingRowGroup.showOnly.value = payload.values.show;
  existingRowGroup.showOnly.operator = FilterOperators.Equal;

  if (values.show === EAllLedgerType.al) {
    existingRowGroup.showOnly.value = Object.values(ELedgerType);
    existingRowGroup.showOnly.operator = InSetFilterOperator.InSet;
  }

  existingRowGroup.grouping = groupColumns;
  existingRowGroup.groupName = values.groupName;
  existingRowGroup.filters = values.filters;
  draft.accountSummary.rowGroups[index] = existingRowGroup as IRowGroup;

  // If rowgroup had been previously expanded, but no longer has grouping, collapse it.
  if (existingRowGroup.grouping.length === 0) {
    draft.accountSummary.expandedRows = removeExpandedState(draft.accountSummary.expandedRows, [existingRowGroup.id]);
  }
}

export interface IOverrideRowColumnPayload {
  datasourceId: string;
  amount: string;
  name: string;
  groupByConfig: GroupByConfig;
  filters: any;
  dateFilter: any;
}

export type IRowOverrideEditorFormValues = IRowEditorFormValues & {
  columnGroupsData: IOverrideRowColumnPayload[];
};

export type IUpdateOverrideRowGroupPayload = { rowGroupIndex: number; values: IRowOverrideEditorFormValues };

export function upsertOverrideRowGroup(
  draft: Draft<IReportConfiguration>,
  payload: IUpdateOverrideRowGroupPayload
): void {
  if (!draft.accountSummary || isMultiHierarchyAccountSummary(draft.accountSummary)) {
    return;
  }
  const accountSummary = draft.accountSummary;

  const { values, rowGroupIndex } = payload;
  const rowGroup = accountSummary.rowGroups[rowGroupIndex] ?? {
    ...JSON.parse(JSON.stringify(getLastRowGroupAsOverride(accountSummary.rowGroups))),
    id: uuidv4()
  };

  if (rowGroup.type !== EGroupType.Override) {
    throw new Error(
      `Row group at index ${String(rowGroupIndex)} should be override but has type '${String(rowGroup.type)}'`
    );
  }

  rowGroup.showOnly.value = values.show;
  rowGroup.groupName = values.groupName;
  rowGroup.showOnly.operator = FilterOperators.Equal;

  rowGroup.filters = values.filters ?? [];

  rowGroup.grouping = values.groupBy.map((groupBy) => groupingFromGroupColumn(groupBy, rowGroup.showOnly.dimension));

  rowGroup.columnGroups = accountSummary.columnGroups.reduce((groupMap, parentColumnGroup) => {
    const { id } = parentColumnGroup;
    const payloadColumnGroup = values.columnGroupsData[id];
    if (!payloadColumnGroup) {
      return groupMap;
    }
    const uniqueId = uuidv4();

    groupMap[id] = updateLensColumnGroup(
      {
        id: uniqueId,
        lens: {} as IRelation,
        measureColumn: {} as ILensColumnReference,
        grouping: [{} as IProjection]
      } as IColumnDataSource,
      // Set name to id so that multiple override row column groups with same data source name won't mess up query
      {
        ...(payloadColumnGroup as IOverrideRowColumnPayload),
        name: uniqueId
      } as ICreateAccountSummaryColumnGroupPayload
    );
    return groupMap;
  }, {});

  accountSummary.rowGroups[rowGroupIndex] = rowGroup;
}

export interface IMultiHierarchyOverrideRowColumnPayload {
  datasourceId: string;
  amount: string;
  groupByConfig?: GroupByConfig;
  /**
   * Every organize-by level the override inherits from its parent, outermost first. An override has
   * to key its columns exactly as the parent does, so a parent broken down three deep gives three
   * levels here.
   */
  groupByConfigs?: GroupByConfig[];
  filters?: MultiHierarchyCoaFilter[];
  dateFilter?: IColumnGroupDateFilter;
  relativeFilter?: RelativeFilter;
  rollingNumRangeOption?: IRollingRangeOption;
  relativeRolling?: string;
}

export type IMultiHierarchyRowOverrideEditorFormValues = {
  displayName?: string;
  /** May be empty — an override row is allowed to be a single labelled row with no drill. */
  grouping?: MultiHierarchyGroupingLevel[];
  filters?: MultiHierarchyCoaFilter[];
  /** Keyed by parent column group id; only the columns the form actually overrode are present. */
  columnGroupsData?: Record<string, IMultiHierarchyOverrideRowColumnPayload | undefined>;
};

export type IUpdateMultiHierarchyOverrideRowGroupPayload = {
  rowGroupIndex: number;
  values: IMultiHierarchyRowOverrideEditorFormValues;
};

/**
 * The override's organize-by levels. Reads the level list when the editor sent one and falls back to
 * the singular config, so a caller that still sends one level keeps producing one level.
 */
function overrideOrganizeByProjections(payload: IMultiHierarchyOverrideRowColumnPayload): IProjection[] {
  const configs = payload.groupByConfigs ?? (payload.groupByConfig ? [payload.groupByConfig] : []);
  return configs.map(buildGroupingProjection);
}

function buildMultiHierarchyOverrideColumnGroup(
  payload: IMultiHierarchyOverrideRowColumnPayload
): MultiHierarchyColumnDataSource {
  const uniqueId = uuidv4();
  // Set name to id so that multiple override row column groups with same data source name won't
  // mess up query. The service also relies on override column group ids differing from parent ids.
  const [columnGroup] = createColumnGroups([
    {
      id: uniqueId,
      type: EGroupType.MultiHierarchy,
      lens: { id: payload.datasourceId },
      measureColumn: { columnName: payload.amount },
      grouping: [{ constant: uniqueId }, ...overrideOrganizeByProjections(payload)],
      filters: payload.filters ?? [],
      dateFilter: payload.dateFilter,
      relativeFilter: payload.relativeFilter,
      rollingNumRangeOption: payload.rollingNumRangeOption,
      relativeRolling: payload.relativeRolling
    }
  ]);
  return columnGroup as MultiHierarchyColumnDataSource;
}

function buildMultiHierarchyOverrideColumnGroups(
  parentColumnGroups: Draft<MultiHierarchyAccountSummary>['columnGroups'],
  columnGroupsData: IMultiHierarchyRowOverrideEditorFormValues['columnGroupsData']
): Record<string, MultiHierarchyColumnDataSource> {
  // Calculated column groups have no data source of their own, so they can never be overridden.
  return parentColumnGroups
    .filter(isMultiHierarchyColumnDataSource)
    .reduce<Record<string, MultiHierarchyColumnDataSource>>((groupMap, parentColumnGroup) => {
      const columnPayload = columnGroupsData?.[parentColumnGroup.id];
      // Overriding is opt-in per column: parent column groups the form left blank are skipped.
      if (!columnPayload?.datasourceId) {
        return groupMap;
      }
      groupMap[parentColumnGroup.id] = buildMultiHierarchyOverrideColumnGroup(columnPayload);
      return groupMap;
    }, {});
}

/**
 * ERP (multi-hierarchy) counterpart of `upsertOverrideRowGroup`. Writes a `MultiHierarchyRowOverride`
 * at `rowGroupIndex`, whose per-column data comes from a different lens than the report's parent
 * column groups while the row itself still drills down like a normal multi-hierarchy row group.
 */
export function upsertMultiHierarchyOverrideRowGroup(
  draft: Draft<IReportConfiguration>,
  payload: IUpdateMultiHierarchyOverrideRowGroupPayload
): void {
  if (!draft.accountSummary || !isMultiHierarchyAccountSummary(draft.accountSummary)) {
    return;
  }
  const accountSummary = draft.accountSummary;
  const { values, rowGroupIndex } = payload;
  // `.at()` rather than `[]` because reading past the end is expected — that's the insert half of
  // the upsert — and it types the result as possibly undefined. The explicit `existingRowGroup &&`
  // is what handles that insert case: the row-source type guards take a required argument.
  const existingRowGroup = accountSummary.rowGroups.at(rowGroupIndex);
  const existingOverride =
    existingRowGroup && isMultiHierarchyRowOverride(existingRowGroup) ? existingRowGroup : undefined;

  const rowGroup: MultiHierarchyRowOverride = {
    ...castDraft(existingOverride),
    id: existingRowGroup?.id ?? uuidv4(),
    type: EGroupType.MultiHierarchyOverride,
    displayName: values.displayName,
    grouping: values.grouping ?? [],
    filters: values.filters ?? [],
    columnGroups: buildMultiHierarchyOverrideColumnGroups(accountSummary.columnGroups, values.columnGroupsData)
  };

  accountSummary.rowGroups[rowGroupIndex] = castDraft(rowGroup);
}

// TODO: add tests
export function updateAccountSummaryExpanded(
  draft: Draft<IReportConfiguration>,
  payload: { accountSummaryBlockIndex: number; nextExpandedValue: boolean }
): void {
  const { accountSummaryBlockIndex, nextExpandedValue } = payload;
  const expandedStates = draft.presentationConfiguration.dimensionalSummaryBlockExpandedStates;
  const isStoredAsExpanded = expandedStates.some((state) => state.controlBlockIndex === accountSummaryBlockIndex);

  if (nextExpandedValue && !isStoredAsExpanded) {
    draft.presentationConfiguration.dimensionalSummaryBlockExpandedStates.push(
      createControlBlockIndex({ controlBlockIndex: accountSummaryBlockIndex })
    );
  } else {
    if (isStoredAsExpanded) {
      draft.presentationConfiguration.dimensionalSummaryBlockExpandedStates =
        draft.presentationConfiguration.dimensionalSummaryBlockExpandedStates.filter(
          (state) => state.controlBlockIndex !== accountSummaryBlockIndex
        );
    }
  }
}

export function removeDimensionalSummaryQuery(draft: Draft<IReportConfiguration>, index: number): void {
  draft.presentationConfiguration.dimensionalSummaryBlockNames = removeAndUpdateBlocks(
    draft.presentationConfiguration.dimensionalSummaryBlockNames,
    index
  ) as IControlBlockAlias[];
  draft.presentationConfiguration.dimensionalSummaryBlockExpandedStates = removeAndUpdateBlocks(
    draft.presentationConfiguration.dimensionalSummaryBlockExpandedStates,
    index
  );
  draft.dimensionalSummaryQueries.splice(index, 1);
}

// TODO: add tests
export function toggleVisualizationQueryExcludedCategorizedByValue(
  draft: Draft<IReportConfiguration>,
  payload: string
): void {
  const catIndex = (
    draft.visualizationQuery.showColumn as unknown as IPercentOfTotalReportColumn
  ).excludedCategorizedByValues.findIndex((c) => c === payload);

  if (catIndex === -1) {
    (draft.visualizationQuery.showColumn as unknown as IPercentOfTotalReportColumn).excludedCategorizedByValues.push(
      payload
    );
  } else {
    (draft.visualizationQuery.showColumn as unknown as IPercentOfTotalReportColumn).excludedCategorizedByValues.splice(
      catIndex,
      1
    );
  }
}

function filterCondition(f: IColumnFilter, columnName: string) {
  return !(f.operator !== BooleanOperators.Or && f.operator !== BooleanOperators.And && f.columnName === columnName);
}

export function clearColumnFiltersAndSort(draft: Draft<IReportConfiguration>, payload: { columnName: string }): void {
  draft.visualizationQuery.filter = draft.visualizationQuery.filter.filter((f) =>
    filterCondition(f, payload.columnName)
  );
  draft.detailTableQuery.filter = draft.detailTableQuery.filter.filter((f) => filterCondition(f, payload.columnName));

  if (payload.columnName === draft.detailTableQuery.sortColumn) {
    draft.detailTableQuery.sortColumn = '';
    draft.detailTableQuery.sortDirection = SortDirection.ASC;
  }

  draft.dimensionalSummaryQueries = draft.dimensionalSummaryQueries.map((query) => {
    if (query.filter) {
      query.filter = query.filter.filter((f) => filterCondition(f, payload.columnName));
    }
    return query;
  });
}

// TODO: add tests
export function clearColumnSort(draft: Draft<IReportConfiguration>): void {
  draft.detailTableQuery.sortDirection = SortDirection.ASC;
  draft.detailTableQuery.sortColumn = '';
  draft.detailTableQuery.paginationLimit = 250;
  draft.detailTableQuery.paginationOffset = 0;
}

// TODO: add tests
export function setVisualizationChartType(draft: Draft<IReportConfiguration>, payload: EReportChartType): void {
  draft.visualizationQuery.chartType = payload;
}

// TODO: add tests
export function setVisualizationQueryShowColumnAsPercentOfTotal(
  draft: Draft<IReportConfiguration>,
  payload: { columns: any[]; percentOfTotal: boolean }
): void {
  // getting the type of columns right is very hard
  const { columns, percentOfTotal } = payload;
  const oldShowColumn = draft.visualizationQuery.showColumn;
  const columnRenames = draft.presentationConfiguration.columnNames;

  if (percentOfTotal) {
    // convert to PercentOfTotalReportColumnRecord
    draft.visualizationQuery.showColumnAsPercentOfTotal = true;
    draft.visualizationQuery.showColumn = createPercentOfTotalReportColumn({
      ...oldShowColumn,
      type: BasicColumnTypes.Percent,
      originalColumnType: oldShowColumn.columnType,
      columnType: EReportColumnTypes.percentOfTotal
    });
  } else if (oldShowColumn.name === EOgqlAggregateColumnOperators.count) {
    // convert back to NOT PercentOfTotalReportColumnRecord
    draft.visualizationQuery.showColumnAsPercentOfTotal = percentOfTotal;
    draft.visualizationQuery.showColumn = createAggregateReportColumn();
  } else {
    let displayName = oldShowColumn.displayName;
    if (columnRenames.find((r) => r.originalName === oldShowColumn.name)) {
      displayName = columnRenames.find((r) => r.originalName === oldShowColumn.name).displayName;
    }

    draft.visualizationQuery.showColumnAsPercentOfTotal = percentOfTotal;
    draft.visualizationQuery.showColumn = columns.find((column) => column.name === oldShowColumn.name);
    draft.visualizationQuery.showColumn.displayName = displayName;
  }
}

export function clearColumnFilter(draft: Draft<IReportConfiguration>, payload: { filter: IColumnFilter }): void {
  const isMatch = isSameColumnFilter(payload.filter);

  draft.visualizationQuery.filter = draft.visualizationQuery.filter.filter((f) => !isMatch(f));
  draft.detailTableQuery.filter = draft.detailTableQuery.filter.filter((f) => !isMatch(f));

  if (draft.dimensionalSummaryQueries.length) {
    draft.dimensionalSummaryQueries[0].filter = draft.dimensionalSummaryQueries[0].filter.filter((f) => !isMatch(f));
  }
}

export function clearColumnFilterByName(draft: Draft<IReportConfiguration>, payload: { columnName: string }): void {
  draft.visualizationQuery.filter = draft.visualizationQuery.filter.filter((f) =>
    filterCondition(f, payload.columnName)
  );
  draft.detailTableQuery.filter = draft.detailTableQuery.filter.filter((f) => filterCondition(f, payload.columnName));

  draft.dimensionalSummaryQueries = draft.dimensionalSummaryQueries.map((query) => {
    if (query.filter) {
      query.filter = query.filter.filter((f) => filterCondition(f, payload.columnName));
    }
    return query;
  });
}

function filterBooleanOperatorCondition(f: IColumnFilterBooleanOperator, columnName: string) {
  return f.columnName !== columnName;
}

export function clearColumnFilterBooleanOperatorsByName(
  draft: Draft<IReportConfiguration>,
  payload: { columnName: string }
): void {
  draft.visualizationQuery.filterBooleanOperators = draft.visualizationQuery.filterBooleanOperators.filter((f) =>
    filterBooleanOperatorCondition(f, payload.columnName)
  );
  draft.detailTableQuery.filterBooleanOperators = draft.detailTableQuery.filterBooleanOperators.filter((f) =>
    filterBooleanOperatorCondition(f, payload.columnName)
  );
}

export function setCategorizedByColumnAndResetGroupByColumns(
  draft: Draft<IReportConfiguration>,
  categorizedByColumn: IReportColumn
): void {
  setVisualizationQueryCategorizedByColumn(draft.visualizationQuery, categorizedByColumn);

  draft.detailTableQuery.groupByColumns = [];
  draft.detailTableQuery.paginationOffset = 0;
}

export type ExpandCollapseRowGroupPayload = {
  path: Array<string>;
  /** Exact public MH child-drill capability for this node; omitted for non-MH / programmatic paths. */
  drillCapability?: PublicMhDrillCapability;
};

export function collapseRowGroup(
  draft: Draft<IReportConfiguration>,
  { path, drillCapability }: ExpandCollapseRowGroupPayload
): void {
  const { expandedRows } = draft.accountSummary;
  draft.accountSummary.expandedRows = removeExpandedState(expandedRows, path);
  if (drillCapability) {
    draft.accountSummary.publicDrillCapabilities = removePublicMhDrillCapabilityAndDescendants(
      draft.accountSummary.publicDrillCapabilities,
      drillCapability
    );
  }
}

export function expandRowGroup(
  draft: Draft<IReportConfiguration>,
  { path, drillCapability }: ExpandCollapseRowGroupPayload
): void {
  const { expandedRows } = draft.accountSummary;
  draft.accountSummary.expandedRows = addExpandedState(expandedRows, path);
  if (drillCapability) {
    draft.accountSummary.publicDrillCapabilities = addPublicMhDrillCapability(
      draft.accountSummary.publicDrillCapabilities,
      drillCapability
    );
  }
}

function syncPublicDrillCapabilityForNode(
  draft: Draft<IReportConfiguration>,
  node: IRowNode | RowNode,
  mode: 'add' | 'remove'
): void {
  if (!isMultiHierarchyAccountSummary(draft.accountSummary)) return;
  const capability = derivePublicMhDrillCapability(node as RowNode, draft.accountSummary);
  if (!capability) return;
  draft.accountSummary.publicDrillCapabilities =
    mode === 'add'
      ? addPublicMhDrillCapability(draft.accountSummary.publicDrillCapabilities, capability)
      : removePublicMhDrillCapabilityAndDescendants(draft.accountSummary.publicDrillCapabilities, capability);
}
/**
 * A row that bulk expansion should open. Beyond the shared eligibility check this excludes level-0
 * breakdown stand-ins: opening their root deletes them, so a run targeting both makes the two undo
 * each other and the root ends up closed with the stand-in expanded in its place.
 */
const isBulkExpandTarget = (node: IRowNode): boolean =>
  Boolean(node.key) && !node.expanded && isEligibleNode(node) && !isInjectedRootBreakdownRow(node);

// Find child or children with least expansion depth, and increase their level of expansion by one.
export const expandOneLevel = (draft: Draft<IReportConfiguration>, payload: { api: GridApi; node?: RowNode }) => {
  const { node, api } = payload;
  const { expandedRows } = draft.accountSummary;

  if (node && !node.expanded) {
    node.setExpanded(true);
    draft.accountSummary.expandedRows = addExpandedState(expandedRows, createNodePath(node));
    syncPublicDrillCapabilityForNode(draft, node, 'add');
    return;
  }
  const { nodes: nodesToExpand } = collectNodesToExpandOneLevel(api, node);

  nodesToExpand.forEach((currNode) => {
    currNode.setExpanded(true);
    const path = createNodePath(currNode);
    addExpandedState(expandedRows, path);
    syncPublicDrillCapabilityForNode(draft, currNode, 'add');
  });
  draft.accountSummary.expandedRows = expandedRows;
};

// Loaded expanded rows at the deepest level below `node` (or across the report); level -1 when none.
function collectNodesToCollapseOneLevel(api: GridApi, node?: IRowNode): { level: number; nodes: Set<IRowNode> } {
  const nodesToCollapse = new Set<IRowNode>();
  let levelToCollapse = -1;

  api.forEachNode((currNode) => {
    const isExpandedCandidate = currNode.expanded && isEligibleNode(currNode, node);
    if (!isExpandedCandidate || currNode.level < levelToCollapse) return;
    // If we've found an expanded node at a deeper level
    if (currNode.level > levelToCollapse) {
      nodesToCollapse.clear();
    }
    levelToCollapse = currNode.level;
    nodesToCollapse.add(currNode);
  });

  return { level: levelToCollapse, nodes: nodesToCollapse };
}

/**
 * Deepest level an expandable row of this row group can have: level 0 is the row group itself and
 * its last grouping column produces leaf rows. A row group the report no longer lists keeps its
 * stored depth, since nothing says how deep it went.
 */
function getRowGroupExpandableDepth(accountSummary: IAccountSummary, rowGroupId: string): number {
  const rowGroup = accountSummary.rowGroups.find((group) => group.id === rowGroupId);
  if (!rowGroup) return GROUPING_LIMIT;
  return 'grouping' in rowGroup && Array.isArray(rowGroup.grouping) ? rowGroup.grouping.length - 1 : -1;
}

/**
 * Deepest level stored as expanded below `scopePath`, including rows not loaded into the grid yet.
 * Stored paths can outlive the rows they named (a row group's grouping made shallower, for one),
 * so a path only counts as far as its row group can still expand; otherwise a phantom depth would
 * win over the loaded rows and the collapse would change nothing on screen.
 * Multi-hierarchy reports return -1: each stored expansion there is paired with a public drill
 * capability that can only be derived from a loaded row, so unloaded expansions cannot be removed
 * without the two drifting apart (BUD-18770).
 */
function getDeepestStoredExpandedLevel(
  accountSummary: Draft<IReportConfiguration>['accountSummary'],
  scopePath: string[]
): number {
  if (!accountSummary || isMultiHierarchyAccountSummary(accountSummary)) return -1;
  const { expandedRows } = accountSummary;
  if (scopePath.length > 0) {
    const storedLevel = getDeepestExpandedLevel(expandedRows, scopePath);
    return Math.min(storedLevel, getRowGroupExpandableDepth(accountSummary, scopePath[0]));
  }
  return Object.keys(expandedRows).reduce((deepest, rowGroupId) => {
    const storedLevel = Math.max(getDeepestExpandedLevel(expandedRows, [rowGroupId]), 0);
    const level = Math.min(storedLevel, getRowGroupExpandableDepth(accountSummary, rowGroupId));
    return Math.max(deepest, level);
  }, -1);
}

function removeStoredExpandedLevel(
  accountSummary: Draft<IReportConfiguration>['accountSummary'],
  scopePath: string[],
  level: number
): void {
  if (!accountSummary) return;
  removeExpandedStateAtLevel(accountSummary.expandedRows, scopePath, level);
}

// Find the deepest level of nodes where a group is expanded and collapse all nodes at that level
export const collapseOneLevel = (draft: Draft<IReportConfiguration>, payload: { api: GridApi; node?: RowNode }) => {
  const { node, api } = payload;
  const { expandedRows } = draft.accountSummary;
  if (node && !node.expanded) return;

  const loaded = collectNodesToCollapseOneLevel(api, node);
  // The grid only holds rows that have been scrolled into view, so the deepest expanded level may
  // belong to rows further down that exist only in the stored state.
  const scopePath = node ? createNodePath(node) : [];
  const storedLevel = getDeepestStoredExpandedLevel(draft.accountSummary, scopePath);
  const levelToCollapse = Math.max(loaded.level, storedLevel);

  const nodesToCollapse = loaded.level === levelToCollapse ? loaded.nodes : new Set<IRowNode>();
  if (node && levelToCollapse < 0) nodesToCollapse.add(node);

  nodesToCollapse.forEach((currNode) => {
    currNode.setExpanded(false);
    const path = createNodePath(currNode);
    removeExpandedState(expandedRows, path);
    syncPublicDrillCapabilityForNode(draft, currNode, 'remove');
  });
  if (storedLevel >= 0 && storedLevel === levelToCollapse) {
    removeStoredExpandedLevel(draft.accountSummary, scopePath, levelToCollapse);
  }
  draft.accountSummary.expandedRows = expandedRows;
};

export const expandAll = (draft: Draft<IReportConfiguration>, payload: { api: GridApi }) => {
  const { api } = payload;
  const { expandedRows } = draft.accountSummary;

  // Collected first, then expanded, like expandOneLevel above: opening a multi-hierarchy root
  // removes its injected level-0 breakdown rows through a server-side transaction, and mutating
  // the row model while forEachNode walks it corrupts the traversal.
  const nodesToExpand: IRowNode[] = [];
  api.forEachNode((row) => {
    if (isBulkExpandTarget(row)) {
      nodesToExpand.push(row);
    }
  });

  nodesToExpand.forEach((row) => {
    row.setExpanded(true);
    addExpandedState(expandedRows, createNodePath(row));
    syncPublicDrillCapabilityForNode(draft, row, 'add');
  });

  draft.accountSummary.expandedRows = expandedRows;
};

export const collapseAll = (draft: Draft<IReportConfiguration>, payload: { api: GridApi }) => {
  const { api } = payload;

  // Collected first for the same reason as expandAll, mirrored: closing a multi-hierarchy root
  // re-injects its cached level-0 breakdown rows through a server-side transaction, so collapsing
  // inline would insert rows into the row model while forEachNode is still walking it.
  const nodesToCollapse: IRowNode[] = [];
  api.forEachNode((row) => {
    if (row.expanded) {
      nodesToCollapse.push(row);
    }
  });

  nodesToCollapse.forEach((row) => {
    row.setExpanded(false);
  });

  draft.accountSummary.expandedRows = {};
  draft.accountSummary.publicDrillCapabilities = undefined;
};

export function setAsOfDate(draft: Draft<IReportConfiguration>, asOfDate: string): void {
  draft.accountSummary.asOfDate = asOfDate;
}

export function setAccountSummaryChartType(
  draft: Draft<IReportConfiguration>,
  payload: { chartType: IAccountSummaryChart['type'] }
) {
  if (!draft.accountSummary) {
    return draft;
  }

  // Currently, we're only operating on a single chart, so it's safe to assume the first one.
  const chart = draft.accountSummary.charts?.[0];

  if (chart) {
    // This will likely get more sophisticated as we add more chart types.
    // We may not be able to move between them without updating the chart
    // configuration in more significant ways.
    draft.accountSummary.charts = [switchChartType(chart, payload.chartType, draft.accountSummary)];
  } else {
    // Starting a new chart
    draft.accountSummary.charts = [createChartConfigForChartType(draft.accountSummary, payload.chartType)];
  }
  return draft;
}

// Function to update the `accountSummary.charts` property on the report configuration
export function setAccountSummaryChart(draft: Draft<IReportConfiguration>, payload: { chart: IAccountSummaryChart }) {
  if (!draft.accountSummary) {
    return draft;
  }

  // Create a deep copy of the chart payload to work around an issue with react-hook-form.
  // https://github.com/orgs/react-hook-form/discussions/3715
  const chart = JSON.parse(JSON.stringify(payload.chart)) as IAccountSummaryChart;

  draft.accountSummary.charts = [chart];

  return draft;
}

type IAddNewMultiHierarchyRowGroupPayload = {
  grouping: MultiHierarchyGroupingLevel[];
  filters: MultiHierarchyCompositeFilter[];
  displayName?: string;
};

export function addNewMultiHierarchyRowGroup(
  draft: Draft<IReportConfiguration>,
  payload: IAddNewMultiHierarchyRowGroupPayload
): void {
  if (!draft.accountSummary || !isMultiHierarchyAccountSummary(draft.accountSummary)) {
    return;
  }
  const accountSummary = draft.accountSummary;
  const newRowGroup: MultiHierarchyRowGroup = {
    id: uuidv4(),
    type: EGroupType.MultiHierarchy,
    grouping: payload.grouping,
    filters: payload.filters,
    displayName: payload.displayName
  };
  const newRowGroupDraft = newRowGroup as Draft<MultiHierarchyRowGroup>;
  moveRowLevelNameToFirstRoot(newRowGroupDraft);
  (accountSummary.rowGroups as Draft<MultiHierarchyRowGroup>[]).push(newRowGroupDraft);
}

type IAddMultiHierarchyCalculatedRowGroupPayload = {
  id: string;
  formula: string;
  alias: string;
};

export function upsertMultiHierarchyCalculatedRowGroup(
  draft: Draft<IReportConfiguration>,
  payload: IAddMultiHierarchyCalculatedRowGroupPayload
): void {
  if (!draft.accountSummary || !isMultiHierarchyAccountSummary(draft.accountSummary)) {
    return;
  }
  const accountSummary = draft.accountSummary;
  const rowGroups = accountSummary.rowGroups;
  const existingIndex = rowGroups.findIndex((rg: { id?: string }) => rg.id === payload.id);
  const rowGroup: MultiHierarchyCalculatedRowGroup = {
    id: payload.id,
    type: EGroupType.MultiHierarchyCalculated,
    formula: payload.formula,
    alias: payload.alias
  };

  if (existingIndex >= 0) {
    rowGroups[existingIndex] = rowGroup;
  } else {
    rowGroups.push(rowGroup);
  }
}

type IAddMultiHierarchyCalculatedColumnGroupPayload = {
  id: string;
  formula: string;
  alias: string;
  display?: EExpressionDisplayType;
};

export function upsertMultiHierarchyCalculatedColumnGroup(
  draft: Draft<IReportConfiguration>,
  payload: IAddMultiHierarchyCalculatedColumnGroupPayload
): void {
  if (!draft.accountSummary || !isMultiHierarchyAccountSummary(draft.accountSummary)) {
    return;
  }
  const accountSummary = draft.accountSummary;
  const columnGroups = accountSummary.columnGroups;
  const existingIndex = columnGroups.findIndex((cg: { id?: string }) => cg.id === payload.id);
  const columnGroup: MultiHierarchyCalculatedColumnGroup = {
    id: payload.id,
    type: EGroupType.MultiHierarchyCalculatedColumn,
    formula: payload.formula,
    alias: payload.alias,
    display: payload.display
  };

  if (existingIndex >= 0) {
    columnGroups[existingIndex] = columnGroup;
  } else {
    columnGroups.push(columnGroup);
  }
}

type IUpdateMultiHierarchyRowGroupGroupingPayload = {
  index: number;
  grouping: MultiHierarchyGroupingLevel[];
};

/**
 * The row group at `index`, but only when it is a PLAIN multi-hierarchy row group.
 *
 * `MultiHierarchyRowOverride` structurally satisfies `MultiHierarchyRowGroup` — same `grouping`,
 * `filters` and `displayName` — so a bare cast reached an override row happily and edited it as a
 * plain row, leaving `type: 'multi-hierarchy-override'` intact. That silent mistyping is precisely
 * what the distinct discriminant exists to prevent.
 *
 * Panel routing already sends override rows to their own editor, so arriving here with one is a bug.
 * The mutators no-op rather than write a half-converted row (BUD-18427).
 */
function plainMultiHierarchyRowGroupAt(
  draft: Draft<IReportConfiguration>,
  index: number
): Draft<MultiHierarchyRowGroup> | undefined {
  const rowGroup = draft.accountSummary?.rowGroups[index];
  if (!rowGroup || !isMultiHierarchyRowGroup(rowGroup)) {
    return undefined;
  }
  return rowGroup;
}

function writeGroupDisplayName(rowGroup: Draft<MultiHierarchyRowGroup>, groupId: string, displayName: string): void {
  if (displayName) {
    rowGroup.groupDisplayNames ??= {};
    rowGroup.groupDisplayNames[groupId] = displayName;
    return;
  }
  delete rowGroup.groupDisplayNames?.[groupId];
  const hasRemainingEntries = rowGroup.groupDisplayNames && Object.keys(rowGroup.groupDisplayNames).length > 0;
  if (!hasRemainingEntries) {
    delete rowGroup.groupDisplayNames;
  }
}

/**
 * A row-level `displayName` labels every root row the grid renders, so once a row group has several
 * roots it would repeat one name over all of them. It moves onto the first root instead — the row it
 * was given to — without overwriting a name that root already has of its own.
 */
function moveRowLevelNameToFirstRoot(rowGroup: Draft<MultiHierarchyRowGroup>): void {
  const firstRootRowId = getMultiHierarchyFirstRootRowId(rowGroup);
  if (!firstRootRowId || rowGroup.displayName == null) {
    return;
  }
  if (rowGroup.displayName && !rowGroup.groupDisplayNames?.[firstRootRowId]) {
    writeGroupDisplayName(rowGroup, firstRootRowId, rowGroup.displayName);
  }
  delete rowGroup.displayName;
}

/**
 * The first root's name doubles as the row group's own name (see `getRowGroupDisplayName`), so it
 * follows the group when a grouping change removes that root or merges it into another row: onto
 * the new first root when several remain, otherwise back onto the row group. Names of the other
 * roots stay tied to their roots.
 */
function carryFirstRootNameOver(rowGroup: Draft<MultiHierarchyRowGroup>, previousFirstRootRowId?: string): void {
  if (!previousFirstRootRowId) {
    return;
  }
  const carriedName = rowGroup.groupDisplayNames?.[previousFirstRootRowId];
  const wasFirstRootRemoved = getMultiHierarchyRootRowId(rowGroup, previousFirstRootRowId) !== previousFirstRootRowId;
  if (!carriedName || !wasFirstRootRemoved) {
    return;
  }
  writeGroupDisplayName(rowGroup, previousFirstRootRowId, '');
  const firstRootRowId = getMultiHierarchyFirstRootRowId(rowGroup);
  if (!firstRootRowId) {
    // No row-level name to preserve: the previous grouping had several roots, which never keep one.
    rowGroup.displayName = carriedName;
    return;
  }
  if (!rowGroup.groupDisplayNames?.[firstRootRowId]) {
    writeGroupDisplayName(rowGroup, firstRootRowId, carriedName);
  }
}

function getOnlyRootGroupId(rowGroup: Draft<MultiHierarchyRowGroup>): string | undefined {
  const rootGroupIds = (rowGroup.grouping.length > 0 ? rowGroup.grouping[0].groupIds : undefined) ?? [];
  return rootGroupIds.length === 1 ? rootGroupIds[0] : undefined;
}

/**
 * A row group with a single root renders one root row, which the row editor names at row level. A
 * name an earlier several-root grouping left under that root's id outranks `displayName` in the grid
 * (IRD-1638), so it becomes the row-level name, replacing one it already hid: the row keeps the label
 * it shows, and a rename from the row editor then sticks. That hidden `displayName` may be the name of
 * a removed first root `carryFirstRootNameOver` moved there, which must not relabel the survivor.
 */
function moveOnlyRootNameToRowGroup(rowGroup: Draft<MultiHierarchyRowGroup>): void {
  const onlyRootGroupId = getOnlyRootGroupId(rowGroup);
  const onlyRootName = onlyRootGroupId === undefined ? undefined : rowGroup.groupDisplayNames?.[onlyRootGroupId];
  if (!onlyRootName) {
    return;
  }
  rowGroup.displayName = onlyRootName;
  writeGroupDisplayName(rowGroup, onlyRootGroupId, '');
}

/**
 * The root row a per-group name is written to. The group id is the root row the editor was opened
 * on, captured before any grouping change applied alongside this name. A merge may have absorbed
 * that row since, so the name goes to the row it renders as now. Without a group id — or once that
 * root is gone — the name is meant for the row group as a whole, which on a several-root group is
 * the first root row.
 */
function perGroupNameTarget(rowGroup: Draft<MultiHierarchyRowGroup>, groupId?: string): string | undefined {
  const editedRootRowId = groupId === undefined ? undefined : getMultiHierarchyRootRowId(rowGroup, groupId);
  return editedRootRowId ?? getMultiHierarchyFirstRootRowId(rowGroup);
}

export function updateMultiHierarchyRowGroupGrouping(
  draft: Draft<IReportConfiguration>,
  payload: IUpdateMultiHierarchyRowGroupGroupingPayload
): void {
  const rowGroup = plainMultiHierarchyRowGroupAt(draft, payload.index);
  if (!rowGroup) return;
  const previousFirstRootRowId = getMultiHierarchyFirstRootRowId(rowGroup);
  moveRowLevelNameToFirstRoot(rowGroup);
  rowGroup.grouping = castDraft(payload.grouping);
  carryFirstRootNameOver(rowGroup, previousFirstRootRowId);
  moveRowLevelNameToFirstRoot(rowGroup);
  moveOnlyRootNameToRowGroup(rowGroup);
}

type IUpdateMultiHierarchyRowGroupFiltersPayload = {
  index: number;
  filters: MultiHierarchyCompositeFilter[];
};

export function updateMultiHierarchyRowGroupFilters(
  draft: Draft<IReportConfiguration>,
  payload: IUpdateMultiHierarchyRowGroupFiltersPayload
): void {
  const rowGroup = plainMultiHierarchyRowGroupAt(draft, payload.index);
  if (!rowGroup) return;
  rowGroup.filters = castDraft(payload.filters);
}

type IUpdateMultiHierarchyRowGroupDisplayNamePayload = {
  index: number;
  displayName: string;
  groupId?: string;
};

export function updateMultiHierarchyRowGroupDisplayName(
  draft: Draft<IReportConfiguration>,
  payload: IUpdateMultiHierarchyRowGroupDisplayNamePayload
): void {
  const rowGroup = plainMultiHierarchyRowGroupAt(draft, payload.index);
  if (!rowGroup) return;
  moveRowLevelNameToFirstRoot(rowGroup);

  // A single root row is named at row level, so a name left under its id must not outrank the new one.
  const onlyRootGroupId = getOnlyRootGroupId(rowGroup);
  if (onlyRootGroupId !== undefined) {
    writeGroupDisplayName(rowGroup, onlyRootGroupId, '');
  }
  const targetGroupId = onlyRootGroupId === undefined ? perGroupNameTarget(rowGroup, payload.groupId) : undefined;
  if (targetGroupId) {
    writeGroupDisplayName(rowGroup, targetGroupId, payload.displayName);
  } else if (payload.displayName) {
    rowGroup.displayName = payload.displayName;
  } else {
    delete rowGroup.displayName;
  }
}

type IUpdateMultiHierarchyColumnGroupFiltersPayload = {
  columnGroupIndex: number;
  /** CoA-segment and/or non-CoA lens-column filters; the query pipelines partition them by shape. */
  filters: MultiHierarchyReportFilter[];
  showTotalsColumn?: boolean;
};

export function updateMultiHierarchyColumnGroupFilters(
  draft: Draft<IReportConfiguration>,
  payload: IUpdateMultiHierarchyColumnGroupFiltersPayload
): void {
  const columnGroup = draft.accountSummary?.columnGroups[payload.columnGroupIndex] as MultiHierarchyColumnDataSource;
  columnGroup.filters = payload.filters;
  if (payload.showTotalsColumn !== undefined) {
    columnGroup.showTotalsColumn = payload.showTotalsColumn;

    if (!payload.showTotalsColumn) {
      clearTotalsColumnValueMapping(columnGroup);
    }
  }
}

function clearTotalsColumnValueMapping(columnGroup: MultiHierarchyColumnDataSource): void {
  const mappingKey = getColumnValueMappingKey(columnGroup);
  const columnValueMapping = columnGroup.columnValueMapping;
  if (!mappingKey || !columnValueMapping) return;

  const segmentMapping = columnValueMapping[mappingKey];
  if (!segmentMapping || typeof segmentMapping !== 'object') return;

  const { [TOTALS_COLUMN_VALUE_KEY]: _totalsHeader, ...remainingSegmentMapping } = segmentMapping;

  if (Object.keys(remainingSegmentMapping).length === 0) {
    const { [mappingKey]: _removedSegment, ...remainingMapping } = columnValueMapping;
    columnGroup.columnValueMapping = remainingMapping;
  } else {
    columnGroup.columnValueMapping = { ...columnValueMapping, [mappingKey]: remainingSegmentMapping };
  }
}

type IUpdateMultiHierarchyColumnGroupDateFilterPayload = {
  columnGroupIndex: number;
  dateFilter: IColumnGroupDateFilter;
  relativeFilter: RelativeFilter;
  rollingNumRangeOption: IRollingNumRangeOption;
  relativeRolling?: string;
};

export function updateMultiHierarchyColumnGroupDateFilter(
  draft: Draft<IReportConfiguration>,
  payload: IUpdateMultiHierarchyColumnGroupDateFilterPayload
): void {
  const columnGroup = draft.accountSummary?.columnGroups[payload.columnGroupIndex] as MultiHierarchyColumnDataSource;
  columnGroup.dateFilter = payload.dateFilter;
  columnGroup.relativeFilter = payload.relativeFilter;
  columnGroup.rollingNumRangeOption = payload.rollingNumRangeOption;
  columnGroup.relativeRolling = payload.relativeRolling;
}

type IUpdateMultiHierarchyColumnGroupPayload = IUpdateAccountSummaryColumnGroupPayload & {
  showTotalsColumn?: boolean;
  hasSetupChanges: boolean;
};

/**
 * The parent's organize-by, but keeping the override's own date column when both are
 * dimension-backed.
 *
 * The projection is a pair, and only one half is the parent's to dictate. `columnName` and
 * `dimension` name a column on a dimension every lens shares, so they must follow the parent or the
 * override's values stop matching the report's columns. `joinColumnName` is a column on the
 * override's OWN lens — `period_end_date` on a gl-balances lens, `end_date` on a budget-summary one —
 * so copying the parent's would point at a column that lens may not have.
 *
 * Falls back to the parent's column when the override has none to keep, which happens when the
 * parent switches from a segment to a date: the override lens's date columns are not knowable from
 * the config alone. Such a row renders blank until its editor is reopened, where the Date Field
 * select resolves it.
 */
function withOverrideJoinColumn(parentProjection: IProjection, overrideProjection?: IProjection): IProjection {
  const overrideJoinColumn =
    overrideProjection && 'joinColumnName' in overrideProjection ? overrideProjection.joinColumnName : undefined;

  if (!('joinColumnName' in parentProjection) || !overrideJoinColumn) {
    return cloneDeep(parentProjection);
  }
  return { ...cloneDeep(parentProjection), joinColumnName: overrideJoinColumn };
}

/**
 * True when the parent's new organize-by reads a lens-specific date column that this override has no
 * equivalent for. Happens when the parent switches from a segment to a date: the override's date
 * columns are not knowable from config alone, so the parent's is copied and may not exist on that
 * lens. Those rows are reported to the user so they can pick a Date Field in the row editor.
 */
function needsDateFieldAfterOrganizeByChange(parentProjection: IProjection, overrideProjection?: IProjection): boolean {
  if (!('joinColumnName' in parentProjection)) return false;
  return !(overrideProjection && 'joinColumnName' in overrideProjection);
}

/**
 * What the most recent organize-by propagation touched.
 *
 * These reducers run through generated action creators that discard return values, so the summary is
 * handed off here for the dispatching panel to read and surface.
 *
 * Module state is a compromise, not a preference. It is kept safe by two rules rather than by
 * discipline at the call sites:
 *
 * 1. `updateMultiHierarchyColumnGroup` clears it FIRST, so a value can only ever describe the dispatch
 *    that just ran. A caller that never reads it cannot leave anything behind for a later Apply to
 *    surface, and neither can a dispatch that returns early.
 * 2. The summary reports only rows whose organize-by or date settings actually moved, so replaying the
 *    reducer over an already-propagated config (undo/redo) yields an empty summary and no toast.
 *
 * It is deliberately NOT stored on the draft: this is transient UI state, and report configuration is
 * persisted, so parking a toast payload there risks saving it to the server.
 */
let lastOverrideRowPropagation: OverrideRowPropagation | undefined;

/** Reads and clears the last propagation summary. */
export function takeOverrideRowPropagation(): OverrideRowPropagation | undefined {
  const propagation = lastOverrideRowPropagation;
  lastOverrideRowPropagation = undefined;
  return propagation;
}

/** Which override rows a parent organize-by change rewrites, and which of those need user input. */
export type OverrideRowPropagation = {
  /** Display names (or ids) of override rows whose organize-by was rewritten. */
  rewritten: string[];
  /** Subset of `rewritten` that cannot be resolved from config and need a Date Field chosen. */
  needsDateField: string[];
};

function overrideRowLabel(rowGroup: { displayName?: string; id?: string }, index: number): string {
  // Empty strings must fall through, not win: an unnamed override row arrives with `displayName: ''`
  // as often as with the key absent, and `??` would label it with a blank. Hence explicit truthiness
  // checks rather than nullish coalescing.
  if (rowGroup.displayName) return rowGroup.displayName;
  if (rowGroup.id) return rowGroup.id;
  return `Row ${String(index + 1)}`;
}

/**
 * Keeps ERP override rows in step when a parent column group's organize-by changes, and reports what
 * it touched so the caller can tell the user.
 *
 * An override column group stores its own organize-by, and nothing updated it when the parent's
 * changed. The rollup pipeline matches an override's values against the PARENT's column values
 * (`CASE WHEN "column_grouping_0" = <parentId> AND "column_grouping_1" = ...`), so a stale override
 * matched nothing and every cell in the row rendered blank until the user reopened the row editor
 * and pressed Apply again (BUD-18502).
 *
 * The parent's date filter is propagated for the same reason: `toColumnGroupsData` copies it onto the
 * override at Apply time, so leaving it stale makes the override query a different period than its
 * header claims — wrong numbers rather than blanks. Date settings therefore copy on EVERY parent
 * update, while the organize-by and its dependent `columnValueMapping` are only rewritten when the
 * organize-by actually changed.
 */
function propagateOrganizeByToOverrideRows({
  draft,
  parentColumnGroupId,
  parentProjections,
  parentDateSettings,
  organizeByChanged
}: {
  draft: Draft<IReportConfiguration>;
  parentColumnGroupId: string;
  parentProjections: IProjection[];
  parentDateSettings: ParentDateSettings;
  organizeByChanged: boolean;
}): OverrideRowPropagation {
  const result: OverrideRowPropagation = { rewritten: [], needsDateField: [] };

  draft.accountSummary?.rowGroups.forEach((rowGroup, index) => {
    if (!isMultiHierarchyRowOverride(rowGroup)) return;

    // Typed as required, but a saved override column group can lack `grouping`, so it is read
    // through an explicitly optional shape rather than by trusting the type.
    const overrideColumnGroup = rowGroup.columnGroups[parentColumnGroupId] as
      | (ParentDateSettings & { grouping?: IProjection[]; columnValueMapping?: Record<string, unknown> })
      | undefined;
    if (!overrideColumnGroup?.grouping) return;

    // Level by level: an override keeps its OWN join column at each depth, so the whole list has to
    // be rewritten pairwise rather than just level 1.
    const previous = getOrganizeByProjections(overrideColumnGroup as IColumnSource);
    const dateSettingsChanged = applyParentDateSettings(overrideColumnGroup, parentDateSettings);
    if (organizeByChanged) {
      setOrganizeByProjections(
        overrideColumnGroup as { grouping: IProjection[] },
        parentProjections.map((parentProjection, index) => withOverrideJoinColumn(parentProjection, previous[index]))
      );
      // Keyed by column value, and those just changed.
      overrideColumnGroup.columnValueMapping = {};
    }

    // Nothing actually moved, so stay silent rather than toasting on a no-op Apply.
    if (!organizeByChanged && !dateSettingsChanged) return;

    const label = overrideRowLabel(rowGroup, index);
    result.rewritten.push(label);
    // Only an organize-by change can leave the override reading a date column it has no equivalent
    // for; copying a date range never alters which column is read.
    // Reported once per row, not once per level: the user picks a Date Field per level in the row
    // editor, but the row is what they have to go open.
    const anyLevelNeedsDateField = parentProjections.some((parentProjection, index) =>
      needsDateFieldAfterOrganizeByChange(parentProjection, previous[index])
    );
    if (organizeByChanged && anyLevelNeedsDateField) {
      result.needsDateField.push(label);
    }
  });

  return result;
}

/**
 * Copies the parent's date settings onto an override column group, reporting whether any of them
 * actually moved so the caller can keep quiet on a no-op.
 */
function applyParentDateSettings(
  overrideColumnGroup: ParentDateSettings,
  parentDateSettings: ParentDateSettings
): boolean {
  const dateFilterChanged =
    JSON.stringify(overrideColumnGroup.dateFilter) !== JSON.stringify(parentDateSettings.dateFilter);
  const relativeFilterChanged =
    JSON.stringify(overrideColumnGroup.relativeFilter) !== JSON.stringify(parentDateSettings.relativeFilter);
  const relativeRollingChanged = overrideColumnGroup.relativeRolling !== parentDateSettings.relativeRolling;

  overrideColumnGroup.dateFilter = cloneDeep(parentDateSettings.dateFilter);
  overrideColumnGroup.relativeFilter = cloneDeep(parentDateSettings.relativeFilter);
  overrideColumnGroup.relativeRolling = parentDateSettings.relativeRolling;

  return dateFilterChanged || relativeFilterChanged || relativeRollingChanged;
}

/** The date settings an override column group inherits from its parent. */
type ParentDateSettings = {
  dateFilter?: MultiHierarchyColumnDataSource['dateFilter'];
  relativeFilter?: MultiHierarchyColumnDataSource['relativeFilter'];
  relativeRolling?: string;
};

export function updateMultiHierarchyColumnGroup(
  draft: Draft<IReportConfiguration>,
  payload: IUpdateMultiHierarchyColumnGroupPayload
): void {
  // Cleared first, so whatever a reader sees afterwards can only describe THIS dispatch. See the note
  // on `lastOverrideRowPropagation`.
  lastOverrideRowPropagation = undefined;

  const columnGroup = draft.accountSummary?.columnGroups[
    payload.columnGroupIndex
  ] as Draft<MultiHierarchyColumnDataSource>;
  const previousOrganizeBy = cloneDeep(getOrganizeByProjections(columnGroup as IColumnSource));
  const previousGrouping = draft.metadata?.deletedColumns ? previousOrganizeBy : undefined;

  const incomingOrganizeBy = payload.hasSetupChanges ? incomingOrganizeByProjections(payload) : previousOrganizeBy;
  // Whole list, so adding, removing or reordering a level all count as a change — and a re-Apply
  // that moved nothing still leaves `columnValueMapping` alone.
  const organizeByChanged = JSON.stringify(previousOrganizeBy) !== JSON.stringify(incomingOrganizeBy);

  if (payload.hasSetupChanges) {
    updateAccountSummaryColumnGroup(draft, payload);
    if (organizeByChanged) {
      columnGroup.columnValueMapping = {};
    }
  }
  updateMultiHierarchyColumnGroupFilters(draft, payload);
  updateMultiHierarchyColumnGroupDateFilter(draft, payload);

  // Deliberately outside `payload.hasSetupChanges`: that flag is computed in the column editor as
  // `hasUnappliedGroupBy || hasUnappliedName || hasUnappliedAmount || hasUnappliedDatasource`, so a
  // date-range-only edit set none of it and skipped propagation entirely — every override row kept
  // querying the old period under a header showing the new one. Wrong numbers, and the warning toast
  // never fired because nothing was recorded (BUD-18502).
  //
  // Reads the date settings back off the parent AFTER `updateMultiHierarchyColumnGroupDateFilter` so
  // there is one source of truth for what the parent actually ended up with.
  if (columnGroup.id) {
    lastOverrideRowPropagation = propagateOrganizeByToOverrideRows({
      draft,
      parentColumnGroupId: columnGroup.id,
      parentProjections: incomingOrganizeBy,
      parentDateSettings: {
        dateFilter: columnGroup.dateFilter,
        relativeFilter: columnGroup.relativeFilter,
        relativeRolling: columnGroup.relativeRolling
      },
      organizeByChanged
    });
  }

  if (previousGrouping && draft.metadata?.deletedColumns) {
    clearRetainedColumnGroupIdIfNecessary({
      deletedColumns: draft.metadata.deletedColumns,
      draft,
      payload,
      previousGrouping
    });
  }
}

function clearRetainedColumnGroupIdIfNecessary({
  deletedColumns,
  draft,
  payload,
  previousGrouping
}: {
  deletedColumns: NonNullable<Draft<IReportConfiguration>['metadata']>['deletedColumns'];
  draft: Draft<IReportConfiguration>;
  payload: IUpdateMultiHierarchyColumnGroupPayload;
  previousGrouping: IProjection[];
}): void {
  const columnGroup = draft.accountSummary?.columnGroups[
    payload.columnGroupIndex
  ] as Draft<MultiHierarchyColumnDataSource>;

  if (!columnGroup?.id) {
    return;
  }

  const incomingGrouping = incomingOrganizeByProjections(payload);
  if (JSON.stringify(previousGrouping) === JSON.stringify(incomingGrouping)) {
    return;
  }

  deletedColumns
    .flatMap((ref) => ref.usedAs)
    .filter(
      (usage): usage is Extract<typeof usage, { status: 'retained-but-invalid' }> =>
        usage.status === 'retained-but-invalid'
    )
    .forEach((usage) => {
      usage.retainedColumnGroupIds = usage.retainedColumnGroupIds.filter((id) => id !== columnGroup.id);
    });
}
